import React, { useState, useEffect } from 'react';
import {
  Box,
  HStack,
  VStack,
  Text,
  Button,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  MenuDivider,
  Heading,
  Badge,
  useMediaQuery,
  IconButton,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerOverlay,
  useDisclosure,
  Icon,
} from '@chakra-ui/react';
import { ChevronDownIcon, HamburgerIcon } from '@chakra-ui/icons';
import { FiFileText, FiBarChart2, FiUsers, FiLogOut } from 'react-icons/fi';

const TopNav = ({ isAuthenticated, onNavClick, onSignOut, onRoleChange, currentPage }) => {
  const [isMobile] = useMediaQuery('(max-width: 768px)');
  const [userEmail, setUserEmail] = useState(null);
  const [userRole, setUserRole] = useState(null);
  const { isOpen, onOpen, onClose } = useDisclosure();

  // Fetch user email when authenticated
  useEffect(() => {
    if (isAuthenticated) {
      fetchUserEmail();
    }
  }, [isAuthenticated]);

  // Notify parent component when userRole changes
  useEffect(() => {
    if (onRoleChange) {
      onRoleChange(userRole);
    }
  }, [userRole, onRoleChange]);

  const fetchUserEmail = () => {
    const baseApiUrl = process.env.REACT_APP_BASE_API_URL || 'http://localhost:8000';
    
    const userEmailUrl = window.location.origin.includes(baseApiUrl) ? 
      '/user-email.php' :
      `${baseApiUrl}/user-email.php`;
    
    const tokenStr = localStorage.getItem('google_token');
    const token = tokenStr ? JSON.parse(tokenStr) : null;
    const accessToken = token?.access_token;
    
    const headers = {
      'Content-Type': 'application/json'
    };
    
    if (accessToken) {
      headers['Authorization'] = `Bearer ${accessToken}`;
    }
    
    fetch(userEmailUrl, {
      credentials: 'include',
      headers: headers
    })
    .then(response => response.json())
    .then(data => {
      if (data.email) {
        setUserEmail(data.email);
        fetchUserRolesWithEmail(data.email);
      }
    })
    .catch(error => {
      console.error('Error fetching user email:', error);
    });
  };

  const fetchUserRolesWithEmail = (email) => {
    console.log('Fetching user profile from localStorage for:', email);
    
    // Get user profile from localStorage (set during OAuth login)
    const profileStr = localStorage.getItem('user_profile');
    if (!profileStr) {
      console.log('No user profile found in localStorage');
      return;
    }
    
    try {
      const userProfile = JSON.parse(profileStr);
      console.log('User profile from localStorage:', userProfile);
      
      // Extract roles from the user profile
      if (userProfile.roles && userProfile.roles.length > 0) {
        // Get the first role (user typically has one primary role)
        const userRole = userProfile.roles[0]?.name || null;
        console.log('Setting user role to:', userRole);
        setUserRole(userRole);
      } else {
        console.log('No roles found in user profile');
      }
    } catch (error) {
      console.error('Error parsing user profile:', error);
    }
  };

  const handleNavClick = (item) => {
    onNavClick(item);
    onClose();
  };

  // Debug: Log current userRole value
  useEffect(() => {
    console.log('DEBUG: Current userRole in TopNav:', userRole);
  }, [userRole]);

  const navItems = [
    {
      label: 'Renest Cases',
      icon: FiFileText,
      id: 'cases',
    },
    {
      label: 'Reports',
      icon: FiBarChart2,
      id: 'reports',
    },
    {
      label: 'Manage Roles',
      icon: FiUsers,
      id: 'roles',
    },
    {
      label: 'Manage Users',
      icon: FiUsers,
      id: 'users',
    },
  ];



  return (
    <>
      {isAuthenticated && (
        <Box
          bg="linear-gradient(135deg, #667eea 0%, #764ba2 100%)"
          boxShadow="0 10px 30px rgba(0, 0, 0, 0.15)"
          position="sticky"
          top={0}
          zIndex={200}
          py={4}
          px={isMobile ? 4 : 6}
        >
          <HStack justify="space-between" align="center" maxW="100%" spacing={4}>
            {/* Logo / Title */}
            <Heading
              as="h1"
              size="md"
              color="white"
              fontWeight="700"
              whiteSpace="nowrap"
            >
              Renest Reporter
            </Heading>

            {/* Desktop Navigation */}
            {!isMobile && (
              <HStack spacing={1} ml={8}>
                {navItems
                  .filter(item => {
                    // Hide "Manage Roles" and "Manage Users" for volunteers
                    const shouldShow = !(userRole === 'volunteer' && (item.id === 'roles' || item.id === 'users'));
                    console.log(`DEBUG: Filtering Desktop Navigation item "${item.label}" (id: ${item.id}):`, {
                      userRole,
                      itemId: item.id,
                      shouldShow
                    });
                    return shouldShow;
                  })
                  .map((item) => (
                  <Button
                    key={item.id}
                    leftIcon={<Icon as={item.icon} />}
                    bg={currentPage === item.id ? 'rgba(255, 255, 255, 0.2)' : 'transparent'}
                    color="white"
                    _hover={{
                      bg: 'rgba(255, 255, 255, 0.15)',
                      transform: 'translateY(-2px)',
                    }}
                    fontWeight="600"
                    fontSize="sm"
                    onClick={() => handleNavClick(item.id)}
                    borderRadius="lg"
                  >
                    {item.label}
                  </Button>
                ))}
              </HStack>
            )}

            {/* Spacer */}
            <Box flex={1} />

            {/* User Profile Menu */}
            <Menu>
              <MenuButton
                as={Button}
                rightIcon={<ChevronDownIcon />}
                bg="rgba(255, 255, 255, 0.15)"
                color="white"
                _hover={{
                  bg: 'rgba(255, 255, 255, 0.25)',
                }}
                fontWeight="600"
                fontSize="sm"
                maxW={isMobile ? '120px' : '200px'}
                isTruncated
              >
                {userEmail ? userEmail.split('@')[0] : 'User'}
              </MenuButton>
              <MenuList>
                <Box p={3} bg="gray.50">
                  <Text fontSize="xs" color="gray.600" fontWeight="600">
                    Logged in as
                  </Text>
                  <Text fontSize="sm" color="gray.900" fontWeight="500" isTruncated>
                    {userEmail || 'Loading...'}
                  </Text>
                  {userRole && (
                    <Badge
                      colorScheme={userRole === 'admin' ? 'red' : 'yellow'}
                      fontSize="xs"
                      mt={2}
                      px={2}
                      py={1}
                    >
                      {userRole.charAt(0).toUpperCase() + userRole.slice(1)}
                    </Badge>
                  )}
                </Box>
                <MenuDivider m={0} />
                <MenuItem
                  icon={<Icon as={FiLogOut} />}
                  onClick={onSignOut}
                  color="red.600"
                  fontWeight="600"
                >
                  Sign Out
                </MenuItem>
              </MenuList>
            </Menu>

            {/* Mobile Hamburger Button */}
            {isMobile && (
              <IconButton
                icon={<HamburgerIcon />}
                bg="rgba(255, 255, 255, 0.15)"
                color="white"
                _hover={{
                  bg: 'rgba(255, 255, 255, 0.25)',
                }}
                onClick={onOpen}
                aria-label="Open menu"
                display={{ base: 'flex', md: 'none' }}
              />
            )}
          </HStack>
        </Box>
      )}

      {/* Mobile Drawer */}
      {isMobile && isOpen && (
        <Drawer isOpen={isOpen} placement="left" onClose={onClose}>
          <DrawerOverlay backdropFilter="blur(4px)" />
          <DrawerContent bg="white">
            <DrawerCloseButton />
            <Box bg="linear-gradient(135deg, #667eea 0%, #764ba2 100%)" py={4} px={4}>
              <Heading as="h2" size="md" color="white">
                Renest Reporter
              </Heading>
            </Box>
            <DrawerBody p={0}>
              <VStack align="stretch" spacing={0} p={0}>
                {navItems
                  .filter(item => !(userRole === 'volunteer' && (item.id === 'roles' || item.id === 'users')))
                  .map((item) => (
                  <Button
                    key={item.id}
                    leftIcon={<Icon as={item.icon} />}
                    justifyContent="flex-start"
                    width="100%"
                    borderRadius="0"
                    bg={currentPage === item.id ? 'purple.100' : 'transparent'}
                    color={currentPage === item.id ? 'purple.700' : 'gray.700'}
                    _hover={{ bg: 'purple.50' }}
                    fontWeight="600"
                    py={3}
                    px={4}
                    onClick={() => handleNavClick(item.id)}
                  >
                    {item.label}
                  </Button>
                ))}
                <Box borderTop="1px solid" borderColor="gray.200" w="100%" />
                <Button
                  width="100%"
                  justifyContent="flex-start"
                  leftIcon={<Icon as={FiLogOut} />}
                  borderRadius="0"
                  colorScheme="red"
                  variant="ghost"
                  py={3}
                  px={4}
                  onClick={onSignOut}
                >
                  Sign Out
                </Button>
              </VStack>
            </DrawerBody>
          </DrawerContent>
        </Drawer>
      )}
    </>
  );
};

export default TopNav;
